import { useState, useEffect, useCallback } from "react";
import {
  LayoutDashboard, Building2, Users, Key,
  ScrollText, RefreshCw, ChevronLeft, ChevronRight
} from "lucide-react";
import DashboardLayout from "../../../components/DashboardLayout";
import { supabase } from "../../../lib/supabaseClient";

// const NAV = [
//   { label: "Dashboard",     icon: LayoutDashboard },
//   { label: "Properties",    icon: Building2 },
//   { label: "Users",         icon: Users },
//   { label: "Valet Codes",   icon: Key },
//   { label: "Activity Logs", icon: ScrollText, active: true },
//   { label: "Settings",      icon: Settings },
// ];

const NAV = [
  { label: "Dashboard",     icon: LayoutDashboard, path: "/admin/dashboard" },
  { label: "Properties",    icon: Building2,        path: "/admin/properties" },
  { label: "Users",         icon: Users,            path: "/admin/users" },
  { label: "Valet Codes",   icon: Key,              path: "/admin/valet-codes" },
  { label: "Activity Logs", icon: ScrollText,       path: "/admin/logs" },
  // { label: "Settings",      icon: Settings,         path: "/admin/settings" },
];

const PAGE_SIZE = 20;

// ─── Helpers ──────────────────────────────────────────────────────────────────
function getToday() {
  return new Date().toISOString().split("T")[0];
}

// function addDays(dateStr, n) {
//   const d = new Date(dateStr + "T00:00:00");
//   d.setDate(d.getDate() + n);
//   return d.toISOString().split("T")[0];
// }

function addDays(dateStr, n) {
  const [y, m, d] = dateStr.split("-").map(Number);
  const date = new Date(y, m - 1, d); // local time, no timezone shift
  date.setDate(date.getDate() + n);
  const yy = date.getFullYear();
  const mm  = String(date.getMonth() + 1).padStart(2, "0");
  const dd  = String(date.getDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

function formatDate(dateStr) {
  return new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", {
    weekday: "short", month: "short", day: "numeric",
  });
}

function formatTime(iso) {
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

// ─── Action Config ────────────────────────────────────────────────────────────
function getActionConfig(action) {
  switch (action) {
    case "valet_status_changed":     return { label: "Valet Status Changed",   color: "#ff9800", bg: "#fff3e0", border: "#ffb74d",     dot: "#ff9800" };
    case "resident_trash_submitted": return { label: "Resident Trash Submit",  color: "var(--g600)", bg: "var(--g50)", border: "var(--g200)", dot: "var(--g600)" };
    default: return { label: action?.replace(/_/g, " ") || "Action", color: "#1565c0", bg: "#e3f2fd", border: "#90caf9", dot: "#1565c0" };
  }
}

// ─── Date Navigator ───────────────────────────────────────────────────────────
function DateNavigator({ date, onChange }) {
  const isToday = date === getToday();
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <button onClick={() => onChange(addDays(date, -1))} style={{
        width: 32, height: 32, borderRadius: 8, border: "1.5px solid var(--ln)",
        background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <ChevronLeft size={16} color="var(--mut)" />
      </button>
      <div style={{
        padding: "6px 16px", borderRadius: 8, border: "1.5px solid var(--ln)",
        background: "#fff", fontSize: 13, fontWeight: 600, color: "var(--ink)",
        minWidth: 150, textAlign: "center",
      }}>
        {isToday ? `Today · ${formatDate(date)}` : formatDate(date)}
      </div>
      <button onClick={() => onChange(addDays(date, 1))} disabled={isToday} style={{
        width: 32, height: 32, borderRadius: 8, border: "1.5px solid var(--ln)",
        background: "#fff", cursor: isToday ? "not-allowed" : "pointer",
        display: "flex", alignItems: "center", justifyContent: "center", opacity: isToday ? 0.4 : 1,
      }}>
        <ChevronRight size={16} color="var(--mut)" />
      </button>
      {!isToday && (
        <button onClick={() => onChange(getToday())} style={{
          padding: "6px 12px", borderRadius: 8, border: "1.5px solid var(--g300)",
          background: "var(--g50)", cursor: "pointer", fontSize: 12, fontWeight: 700, color: "var(--g600)",
        }}>Today</button>
      )}
    </div>
  );
}

// ─── Log Row ──────────────────────────────────────────────────────────────────
function LogRow({ log, propMap }) {
  const cfg      = getActionConfig(log.action);
  const propName = log.property_id ? (propMap[log.property_id] || "—") : "—";

  return (
    <div style={{
      display: "flex", alignItems: "flex-start", gap: 12,
      padding: "13px 16px", borderRadius: 11,
      background: "#fff", border: "1px solid var(--ln)",
      marginBottom: 8, boxShadow: "0 1px 3px rgba(0,0,0,.04)",
    }}>
      {/* Dot */}
      <div style={{
        width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
        background: cfg.dot, marginTop: 5,
      }} />

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
          <span style={{
            fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20,
            background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
            textTransform: "uppercase", letterSpacing: ".5px",
          }}>{cfg.label}</span>
          <span style={{ fontSize: 12, color: "var(--mut)" }}>{propName}</span>
        </div>

        {/* Metadata */}
        <div style={{ fontSize: 12, color: "var(--mut)", lineHeight: 1.6 }}>
          {log.action === "valet_status_changed" && log.metadata?.status && (
            <span>Status → <strong style={{ color: "var(--ink)" }}>{log.metadata.status.replace(/_/g, " ")}</strong>
              {log.metadata?.code && <span> · Code: <strong>{log.metadata.code}</strong></span>}
            </span>
          )}
          {log.action === "resident_trash_submitted" && (
            <span>Unit <strong style={{ color: "var(--ink)" }}>#{log.metadata?.unit_number || "—"}</strong> submitted trash</span>
          )}
          {log.action !== "valet_status_changed" && log.action !== "resident_trash_submitted" && (
            <span>{JSON.stringify(log.metadata)}</span>
          )}
        </div>
      </div>

      {/* Time */}
      <div style={{ fontSize: 11.5, color: "var(--mut)", flexShrink: 0, textAlign: "right" }}>
        <div>{formatTime(log.created_at)}</div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function SuperAdminLogs() {
  const today = getToday();

  const [logs,        setLogs]        = useState([]);
  const [properties,  setProperties]  = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [total,       setTotal]       = useState(0);
  const [page,        setPage]        = useState(0);
  const [selectedDate,setSelectedDate]= useState(today);
  const [filterAction,setFilterAction]= useState("all");
  const [filterProp,  setFilterProp]  = useState("all");

  const propMap = Object.fromEntries(properties.map(p => [p.id, p.name]));

  // Fetch properties once
  useEffect(() => {
    supabase.from("properties").select("id, name").order("name")
      .then(({ data }) => setProperties(data || []));
  }, []);

  // Fetch logs
  const fetchLogs = useCallback(async () => {
    setLoading(true);

    const dateStart = `${selectedDate}T00:00:00`;
    const dateEnd   = `${selectedDate}T23:59:59`;

    let query = supabase
      .from("activity_logs")
      .select("id, action, property_id, user_id, metadata, created_at", { count: "exact" })
      .gte("created_at", dateStart)
      .lte("created_at", dateEnd)
      .order("created_at", { ascending: false })
      .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

    if (filterAction !== "all") query = query.eq("action", filterAction);
    if (filterProp   !== "all") query = query.eq("property_id", filterProp);

    const { data, count } = await query;
    setLogs(data || []);
    setTotal(count || 0);
    setLoading(false);
  }, [selectedDate, filterAction, filterProp, page]);

  useEffect(() => { setPage(0); }, [selectedDate, filterAction, filterProp]);
  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const actionFilters = [
    { key: "all",                      label: "All Actions" },
    { key: "valet_status_changed",     label: "Valet Status" },
    { key: "resident_trash_submitted", label: "Trash Submissions" },
  ];

  return (
    <DashboardLayout role="super_admin" title="Activity Logs" navItems={NAV}>

      {/* Header */}
      <div className="fu" style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "2px", textTransform: "uppercase", color: "#6a1b9a", marginBottom: 6 }}>Super Admin</div>
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div>
            <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 24, fontWeight: 500, color: "var(--ink)", margin: 0 }}>Activity Logs</h2>
            <p style={{ fontSize: 13, color: "var(--mut)", marginTop: 4 }}>All system actions across all properties.</p>
          </div>
          <DateNavigator date={selectedDate} onChange={setSelectedDate} />
        </div>
      </div>

      {/* Filters */}
      <div className="fu d1" style={{ display: "flex", gap: 10, marginBottom: 18, flexWrap: "wrap", alignItems: "center" }}>
        {/* Action filter */}
        <div style={{ display: "flex", gap: 8 }}>
          {actionFilters.map(({ key, label }) => (
            <button key={key} onClick={() => setFilterAction(key)} style={{
              padding: "7px 14px", borderRadius: 8, border: "1.5px solid var(--ln)",
              background: filterAction === key ? "#6a1b9a" : "#fff",
              color: filterAction === key ? "#fff" : "var(--mut)",
              fontSize: 12, fontWeight: 700, cursor: "pointer",
            }}>{label}</button>
          ))}
        </div>

        {/* Property filter */}
        <select value={filterProp} onChange={e => setFilterProp(e.target.value)} style={{
          padding: "8px 12px", borderRadius: 8, fontSize: 13,
          border: "1.5px solid var(--ln)", background: "#fafafa",
          color: "var(--ink)", outline: "none", minWidth: 160,
        }}>
          <option value="all">All Properties</option>
          {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>

        {/* Refresh */}
        <button onClick={fetchLogs} style={{
          width: 36, height: 36, borderRadius: 8, border: "1.5px solid var(--ln)",
          background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <RefreshCw size={14} color="var(--mut)" />
        </button>

        {/* Total count */}
        <div style={{ marginLeft: "auto", fontSize: 12, color: "var(--mut)", fontWeight: 600 }}>
          {total} log{total !== 1 ? "s" : ""} found
        </div>
      </div>

      {/* Logs List */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "48px 0", color: "var(--mut)", fontSize: 13 }}>Loading logs…</div>
      ) : logs.length === 0 ? (
        <div style={{
          textAlign: "center", padding: "40px 24px",
          background: "#fff", borderRadius: 14, border: "1.5px dashed var(--ln)",
        }}>
          <ScrollText size={30} color="var(--ln)" style={{ marginBottom: 12 }} />
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--ink)", marginBottom: 6 }}>No logs found</div>
          <div style={{ fontSize: 13, color: "var(--mut)" }}>No activity for selected date and filters.</div>
        </div>
      ) : (
        logs.map(log => <LogRow key={log.id} log={log} propMap={propMap} />)
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginTop: 20 }}>
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} style={{
            padding: "8px 16px", borderRadius: 8, border: "1.5px solid var(--ln)",
            background: "#fff", cursor: page === 0 ? "not-allowed" : "pointer",
            fontSize: 12, fontWeight: 700, color: "var(--mut)", opacity: page === 0 ? 0.4 : 1,
          }}>← Prev</button>

          <span style={{ fontSize: 13, color: "var(--mut)", fontWeight: 600 }}>
            Page {page + 1} of {totalPages}
          </span>

          <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} style={{
            padding: "8px 16px", borderRadius: 8, border: "1.5px solid var(--ln)",
            background: "#fff", cursor: page >= totalPages - 1 ? "not-allowed" : "pointer",
            fontSize: 12, fontWeight: 700, color: "var(--mut)", opacity: page >= totalPages - 1 ? 0.4 : 1,
          }}>Next →</button>
        </div>
      )}

      <style>{`@keyframes fadeUp { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }`}</style>
    </DashboardLayout>
  );
}